import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MainLayout } from 'components/Layout';

const heroVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.23, 1, 0.32, 1] },
  },
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

export const Landing = () => {
  const navigate = useNavigate();

  return (
    <MainLayout
      title="Northup — Intelligence for modern product teams"
      description="Northup is the operating system for high-velocity teams—analytics, automation and AI-native workflows in a single, beautifully crafted interface."
    >
      <div className="flex flex-col gap-14 lg:gap-20">
        {/* HERO */}
        <motion.section
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="grid gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:items-center"
        >
          {/* Left: copy */}
          <motion.div variants={heroVariants} className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/80 bg-slate-900/60 px-3 py-1 text-xs font-medium text-slate-300 shadow-sm backdrop-blur">
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <span>Africa's Emerging Technologies</span>
              <button
                className="ml-2 rounded-full bg-slate-800/80 px-2 py-[2px] text-[10px] uppercase tracking-[0.16em] text-slate-300 hover:bg-slate-700/80"
                onClick={() => navigate('/blog/emerging-technologies-in-africa')}
              >
                Read More
              </button>
            </div>

            <div className="space-y-4">
              <h1 className="text-balance text-4xl font-semibold tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
                Building Technology that
                <span className="relative whitespace-nowrap">
                  <span className="relative bg-gradient-to-r from-[#9D5666] via-[#E1E1E1] to-[#bf7082] bg-clip-text text-transparent">
                    {' '}
                    transforms
                  </span>
                  <span
                    aria-hidden
                    className="absolute -inset-x-1 -bottom-1 h-[55%] rounded-full bg-gradient-to-r from-cyan-500/20 via-sky-500/10 to-purple-500/20 blur-xl"
                  />
                </span>{' '}
                Africa.
              </h1>

              <p className="max-w-xl text-sm text-slate-400 sm:text-base">
                We combine product analytics, engineering workflows, and AI copilots into a single,
                living surface.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button
                onClick={() => navigate('/signup')}
                className="group inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#1e9c41] via-[#a8cbb2] to-[#5aa36e] px-5 py-2.5 text-sm font-semibold text-slate-800 shadow-[0_0_32px_rgba(56,189,248,0.55)] transition hover:brightness-110"
              >
                Chat on Whatsapp
                <span className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900/10 text-[11px]">
                  ↗
                </span>
              </button>

              <button
                onClick={() => navigate('/demo')}
                className="inline-flex items-center justify-center rounded-full border border-slate-700/80 bg-slate-900/60 px-4 py-2 text-sm font-medium text-slate-200 backdrop-blur transition hover:border-slate-500 hover:text-slate-50"
              >
                Request for meeting
              </button>

              <div className="flex items-center gap-3 text-[11px] text-slate-500">
                <div className="flex -space-x-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-900 bg-slate-800 text-[10px]">
                    A
                  </span>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-900 bg-slate-800 text-[10px]">
                    P
                  </span>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-900 bg-slate-800 text-[10px]">
                    S
                  </span>
                </div>
                <span>Trusted by teams at early-stage to Series C.</span>
              </div>
            </div>

            <div className="mt-4 grid gap-4 text-xs text-slate-400 sm:grid-cols-3 sm:text-[11px]">
              <Stat label="Average time-to-deploy" value="3.2× faster" />
              <Stat label="Ops overhead" value="-47%" />
              <Stat label="Team NPS after 90 days" value="+36" />
            </div>
          </motion.div>

          {/* Right: animated "product" card */}
          <motion.div variants={heroVariants} className="relative">
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
              className="relative rounded-[32px] border border-slate-700/70 bg-slate-900/70 p-4 shadow-[0_22px_60px_rgba(15,23,42,0.95)] backdrop-blur-xl sm:p-5 lg:p-6"
            >
              {/* Glow */}
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-px rounded-[32px] bg-gradient-to-tr from-cyan-500/20 via-transparent to-purple-500/30 opacity-80 mix-blend-screen"
              />

              {/* Top bar */}
              <div className="relative mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-400/90" />
                  <span className="text-xs font-medium text-slate-200">Live orchestration map</span>
                </div>
                <span className="rounded-full bg-slate-800/80 px-2 py-1 text-[10px] font-medium text-slate-300">
                  Realtime
                </span>
              </div>

              {/* Mocked app surface */}
              <div className="relative grid gap-4 sm:grid-cols-[1.15fr_0.9fr]">
                <motion.div
                  whileHover={{ scale: 1.01, translateY: -2 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                  className="rounded-2xl border border-slate-700/80 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-950 p-3"
                >
                  <div className="mb-3 flex items-center justify-between text-[11px] text-slate-400">
                    <span>Deployment stream</span>
                    <span className="rounded-full bg-slate-800/80 px-2 py-[2px] text-[10px] text-emerald-300">
                      14 active
                    </span>
                  </div>
                  <div className="space-y-2.5">
                    {['API', 'Web', 'Workers', 'Data'].map((item, idx) => (
                      <motion.div
                        key={item}
                        initial={{ width: `${40 + idx * 10}%` }}
                        animate={{ width: `${70 + idx * 5}%` }}
                        transition={{
                          duration: 2.4,
                          repeat: Infinity,
                          repeatType: 'mirror',
                          delay: idx * 0.2,
                          ease: 'easeInOut',
                        }}
                        className="flex items-center gap-2"
                      >
                        <div className="h-[7px] flex-1 rounded-full bg-slate-800">
                          <div className="h-[7px] rounded-full bg-gradient-to-r from-[#9D5666] via-sky-500 to-purple-500" />
                        </div>
                        <span className="w-12 text-[10px] text-slate-400">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                <div className="flex flex-col gap-3">
                  <motion.div
                    whileHover={{ y: -2 }}
                    className="rounded-2xl border border-slate-700/80 bg-slate-900/90 p-3 text-[11px]"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-slate-300">AI runbooks</span>
                      <span className="rounded-full bg-slate-800 px-2 py-[1px] text-[10px] text-emerald-300">
                        Auto-resolve
                      </span>
                    </div>
                    <p className="text-slate-500">
                      “Rollback API latency in eu-west to previous stable release.”
                    </p>
                  </motion.div>

                  <motion.div
                    whileHover={{ y: -2 }}
                    className="rounded-2xl border border-slate-700/80 bg-slate-900/90 p-3 text-[11px]"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-slate-300">Signals</span>
                      <span className="text-[10px] text-amber-300">+3 new</span>
                    </div>
                    <div className="space-y-1.5 text-slate-400">
                      <div className="flex items-center justify-between">
                        <span>Churn risk: workspace /core</span>
                        <span className="rounded-full bg-emerald-500/15 px-2 py-[1px] text-[10px] text-emerald-300">
                          Actioned
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Latency spike: billing</span>
                        <span className="rounded-full bg-amber-500/15 px-2 py-[1px] text-[10px] text-amber-300">
                          Watching
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* SECONDARY: value props preview */}
        <section className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9D5666]">
                WHY NORTHUP
              </p>
              <p className="mt-1 text-sm text-slate-400">
                A single surface for product, engineering, and operations.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] text-slate-500">
              <span className="rounded-full border border-slate-700/80 px-3 py-1">
                AI-native workflows
              </span>
              <span className="rounded-full border border-slate-700/80 px-3 py-1">
                First-class observability
              </span>
              <span className="rounded-full border border-slate-700/80 px-3 py-1">
                Built for distributed teams
              </span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <FeatureCard
              pill="Operate"
              title="Unified command center"
              description="Replace five dashboards and three spreadsheets with one realtime view of your product, infra, and customer signals."
            />
            <FeatureCard
              pill="Automate"
              title="AI-first runbooks"
              description="Codify incidents, experiments, and workflows into reusable playbooks that your team and copilots can trigger instantly."
            />
            <FeatureCard
              pill="Collaborate"
              title="Living product canvas"
              description="Align roadmaps, metrics, and delivery in one canvas your whole organization can understand at a glance."
            />
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

const Stat = ({ label, value }) => (
  <div className="space-y-1 rounded-2xl border border-slate-800/80 bg-slate-900/60 p-3 backdrop-blur">
    <p className="text-[11px] text-slate-400">{label}</p>
    <p className="text-sm font-semibold text-slate-50">{value}</p>
  </div>
);

const FeatureCard = ({ pill, title, description }) => (
  <motion.article
    whileHover={{
      y: -4,
      scale: 1.01,
      boxShadow: '0 24px 60px rgba(15,23,42,0.85)',
    }}
    transition={{ type: 'spring', stiffness: 260, damping: 20 }}
    className="relative overflow-hidden rounded-2xl border border-slate-800/80 bg-slate-900/60 p-4 text-sm backdrop-blur"
  >
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 -top-10 h-24 bg-gradient-to-r from-cyan-500/15 via-purple-500/10 to-fuchsia-500/15 blur-2xl"
    />
    <div className="relative space-y-3">
      <span className="inline-flex items-center rounded-full border border-slate-700/80 bg-slate-900/80 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.16em] text-slate-300">
        {pill}
      </span>
      <h3 className="text-base font-semibold text-slate-50">{title}</h3>
      <p className="text-xs text-slate-400">{description}</p>
    </div>
  </motion.article>
);
